import React, { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { User } from '../types/auth';
import { AuthContext } from './AuthContext';
import { apiService } from '../services/api';
import { datadogRum } from '@datadog/browser-rum';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (apiService.isAuthenticated()) {
      const stored = localStorage.getItem('user');
      if (stored) {
        try {
          const parsed: User = JSON.parse(stored);
          setUser(parsed);
          // Set user info in Datadog RUM on initial load
          datadogRum.setUser({
            id: String(parsed.id),
            email: parsed.email,
            role: parsed.role,
          });
        } catch {
          setUser({ id: '', email: '', role: '' });
        }
      } else {
        setUser({ id: '', email: '', role: '' });
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const response = await apiService.login({ email, password });
    setUser(response.user);
    localStorage.setItem('user', JSON.stringify(response.user));
    
    // Set user info in Datadog RUM
    datadogRum.setUser({
      id: String(response.user.id),
      email: response.user.email,
      role: response.user.role,
    });
    
    // Track login action
    datadogRum.addAction('user_login', {
      email: response.user.email,
      role: response.user.role,
    });
  };

  const logout = async () => {
    // Track logout action before clearing user
    datadogRum.addAction('user_logout');
    
    // Clear user info in Datadog RUM
    datadogRum.clearUser();
    
    await apiService.logout();
    setUser(null);
    localStorage.removeItem('user');
  };

  const value = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 